import type { AthleteResult } from "@/components/athlete/types";
import numeral from "numeral";

const INVALID_RANK_SENTINEL = 99999;

export const isRankDisplayable = (value: number | null | undefined) =>
  value != null && value < INVALID_RANK_SENTINEL;

export const fmtRank = (value: number | null | undefined) =>
  isRankDisplayable(value) ? `#${value}` : "—";

export const fmtNum = (value: number | null | undefined) =>
  value != null ? value.toLocaleString() : "—";

export const fmtCompactNum = (value: number | null | undefined) => {
  if (value == null) return "—";
  const formatted = numeral(Math.round(value)).format("0a");
  return formatted.replace(/([a-z]+)$/i, (suffix) => suffix.toUpperCase());
};

export const fmtCompactNumWithOneDecimal = (
  value: number | null | undefined,
  decimalThreshold = 100_000,
) => {
  if (value == null) return "—";
  const pattern = Math.abs(value) < decimalThreshold ? "0.0a" : "0a";
  const formatted = numeral(value).format(pattern);
  return formatted.replace(/([a-z]+)$/i, (suffix) => suffix.toUpperCase());
};

export const fmtDate = (value: string | null | undefined) => {
  if (!value) return "Date unknown";
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
};

export const formatDateTick = (
  eventDate: string | null | undefined,
  year: number | null | undefined,
  index: number,
): string => {
  if (eventDate) {
    return String(new Date(`${eventDate}T00:00:00Z`).getUTCFullYear());
  }
  if (year != null) return String(year);
  return `#${index + 1}`;
};

export const formatSplitSeconds = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

export const getResultStatus = (result: AthleteResult) => {
  if (result.finisher) {
    return isRankDisplayable(result.finishRankOverall)
      ? `#${result.finishRankOverall} overall`
      : "Finisher";
  }
  if (result.dnf) return "DNF";
  if (result.dns) return "DNS";
  if (result.dq) return "DQ";
  return "—";
};
